import { ChatBubble } from "@/components/ai/chat/ChatBubble";
import { OnaTips } from "@/components/ai/chat/OnaTips";
import { TodoContainer } from "@/components/ai/chat/TodoContainer";
import { Banner } from "@/components/flexkit/Banner";
import type { AgentMessageFragment, AgentMessageFragmentTodoItem, Message } from "@/hooks/use-stream-conversation";
import { Agents, type PlainAgentExecution } from "@/queries/agent-queries";
import { useEnvironment } from "@/queries/environment-queries";
import { filterMessagesByType, MESSAGE_TYPES } from "@/utils/chat";
import { getActiveEnvironmentId } from "@/utils/agent-executions";
import { groupMessagesByTodo } from "@/utils/message-grouping";
import { EnvironmentPhase } from "gitpod-next-api/gitpod/v1/environment_pb";
import { AgentExecution_Phase, AgentResponseBlock_TodoItem_Phase } from "gitpod-next-api/gitpod/v1/agent_pb";
import { type FC, useEffect, useState, useMemo } from "react";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { FragmentFileModification } from "@/components/ai/FragmentFileModification";
import { FragmentAction } from "@/components/ai/FragmentAction";
import { FragmentText } from "@/components/ai/FragmentText";
import { FragmentEnvironmentCreation } from "@/components/ai/FragmentEnvironmentCreation";
import { FragmentHostAuthentication } from "@/components/ai/FragmentHostAuthentication";
import { FragmentCodeAnnotation } from "@/components/ai/FragmentCodeAnnotation";
import { Thinking } from "@/components/ai/chat/Thinking";
import { useOptionalVSCodeIpcContext } from "@/hooks/use-vscode-ipc";
import { useEnvironmentSettings } from "@/hooks/use-environment-settings";
import { useFlagTodoGroupingEnabled } from "@/hooks/use-flag";

// OnaTips fade-out duration when conversation starts
// Kept short (300ms) to minimize gap before first message appears
const ONA_TIPS_FADE_OUT_MS = 300;

type ChatMessagesProps = {
    messages: Message[];
    onClick?: (block: AgentMessageFragment) => void;
    agentExecution: PlainAgentExecution;
    todoItems?: AgentMessageFragmentTodoItem[];
    enableTips?: boolean;
} & PropsWithClassName;

export const ChatMessages: FC<ChatMessagesProps> = ({
    messages,
    onClick,
    agentExecution,
    todoItems,
    className,
    enableTips = true,
}) => {
    const environmentId = getActiveEnvironmentId(agentExecution);
    const { data: environment } = useEnvironment(environmentId);
    const { value: isTodoGroupingEnabled } = useFlagTodoGroupingEnabled();

    // Check if we should show Ona tips
    // Show tips while environment is starting OR until agent starts responding (whichever is longer)
    const isEnvironmentStarting =
        environment?.status?.phase &&
        (environment.status.phase === EnvironmentPhase.STARTING ||
            environment.status.phase === EnvironmentPhase.CREATING ||
            environment.status.phase === EnvironmentPhase.UPDATING);

    const hasAgentResponded = messages.length >= 2; // User message + at least one agent message

    const shouldShowOnaTips =
        enableTips &&
        !hasAgentResponded && // Agent hasn't started responding yet
        agentExecution?.spec?.agentId === Agents.InEnvironmentOna.id && // In-environment agent
        agentExecution?.status?.phase !== AgentExecution_Phase.STOPPED && // Conversation is not stopped
        environment?.status?.phase && // Environment data exists
        isEnvironmentStarting;

    // State for managing fade-out animation
    const [isVisible, setIsVisible] = useState(shouldShowOnaTips);
    const [shouldRender, setShouldRender] = useState(shouldShowOnaTips);

    useEffect(() => {
        if (shouldShowOnaTips) {
            setShouldRender(true);
            setIsVisible(true);
        } else if (shouldRender) {
            // Start fade out
            setIsVisible(false);
            // Remove from DOM after animation completes
            const timer = setTimeout(() => {
                setShouldRender(false);
            }, ONA_TIPS_FADE_OUT_MS);
            return () => clearTimeout(timer);
        }
    }, [shouldShowOnaTips, shouldRender]);

    return (
        <div
            className={cn(
                "mx-auto flex h-full w-full max-w-[700px] flex-col items-start p-4 sm:p-6",
                // When todo grouping is disabled, use gap-4 for consistent spacing (original behavior)
                !isTodoGroupingEnabled && "gap-4",
                className,
            )}
        >
            {isTodoGroupingEnabled ? (
                <GroupedMessages
                    messages={messages}
                    todoItems={todoItems}
                    agentExecution={agentExecution}
                    onClick={onClick}
                    data-tracking-id="default-tracking-id"
                />
            ) : (
                messages.map((message, index) => (
                    <ChatBubble
                        agentExecution={agentExecution}
                        message={message}
                        key={message.id}
                        onClick={onClick}
                        isLast={index === messages.length - 1}
                        agentMessages={filterMessagesByType(messages, MESSAGE_TYPES.AGENT)}
                        data-tracking-id-none // the component delegates the onClick handler to its children
                    />
                ))
            )}

            {shouldRender && (
                <div
                    className={cn(
                        "w-full transition-opacity duration-300 ease-out",
                        isVisible ? "opacity-100" : "opacity-0",
                    )}
                >
                    <OnaTips environment={environment} />
                </div>
            )}
            {agentExecution?.status?.warningMessage && (
                <Banner
                    variant="warning"
                    text={agentExecution.status.warningMessage}
                    data-testid="agent-execution-warning-banner"
                />
            )}
            {agentExecution?.status?.failureMessage && (
                <Banner
                    variant="danger"
                    text={agentExecution.status.failureMessage}
                    data-testid="agent-execution-failure-banner"
                />
            )}
        </div>
    );
};

type GroupedMessagesProps = {
    messages: Message[];
    agentExecution: PlainAgentExecution;
    todoItems?: AgentMessageFragmentTodoItem[];
    onClick?: (block: AgentMessageFragment) => void;
};

const GroupedMessages: FC<GroupedMessagesProps> = ({ messages, agentExecution, todoItems, onClick }) => {
    const environmentId = getActiveEnvironmentId(agentExecution);
    const vscodeIpcContext = useOptionalVSCodeIpcContext();

    const { value: viewMode, setValue: setViewMode } = useEnvironmentSettings(
        environmentId || "",
        "viewmode",
        "vscode",
    );

    // Group messages by todo - MEMOIZED to prevent re-computation on every render
    // Only group by todo if feature flag is enabled
    const groupedMessages = useMemo(() => {
        return groupMessagesByTodo(messages);
    }, [messages]);

    // Helper to check if previous/next group is a todo
    const isPreviousTodo = (index: number) => index > 0 && groupedMessages[index - 1]?.type === "todo-group";
    const isNextTodo = (index: number) =>
        index < groupedMessages.length - 1 && groupedMessages[index + 1]?.type === "todo-group";
    const totalNextTodos = (index: number) => {
        let c = 0;
        for (; isNextTodo(c + index); c++);
        // there's always at least one todo in the block
        return c + 1;
    };
    const completeNextTodos = (index: number) => {
        let c = 0;
        for (let i = index; isNextTodo(i); i++) {
            const msg = groupedMessages[i];
            if (msg.type === "todo-group" && msg.todoStatus === AgentResponseBlock_TodoItem_Phase.DONE) {
                c++;
            }
        }
        if (c > 0) {
            c++;
        }
        return c;
    };

    // Helper to get spacing class for an element
    const getSpacingClass = (index: number, isTodo: boolean) => {
        if (isTodo) {
            // Todo containers handle their own margins
            return "";
        }
        // Non-todo elements always get bottom margin unless they're the last item
        return index < groupedMessages.length - 1 ? "mb-4" : "";
    };

    const isDone =
        agentExecution.status?.phase === AgentExecution_Phase.WAITING_FOR_INPUT ||
        agentExecution.status?.phase === AgentExecution_Phase.STOPPED;

    const isNaturallyCompleted = isDone && agentExecution.spec?.desiredPhase !== AgentExecution_Phase.STOPPED;

    // Calculate total todos count - memoized to prevent re-computation
    const totalTodos = useMemo(() => {
        // Filter out cleared todos to get active todos
        const activeTodos = todoItems?.filter((t) => t.status !== AgentResponseBlock_TodoItem_Phase.CLEARED) ?? [];

        // Fallback to counting todo-groups if todoItems is empty
        const todoGroupCount = groupedMessages.filter((g) => g.type === "todo-group").length;
        return activeTodos.length || todoGroupCount;
    }, [groupedMessages, todoItems]);

    // Helper to render a single fragment
    const renderFragment = (fragment: AgentMessageFragment, key: string, isLastFragment = false) => {
        // Check if this is a summary using regex
        const summaryRegex = /^#+\s*summary(\s|\n|$)/i;
        const isSummary = fragment.type === "text" && summaryRegex.exec(fragment.content) !== null;
        const shouldHighlight = isSummary || (isNaturallyCompleted && isLastFragment);

        switch (fragment.type) {
            case "text":
                return (
                    <FragmentText
                        key={key}
                        fragment={fragment}
                        agentExecution={agentExecution}
                        shouldHighlight={shouldHighlight}
                    />
                );
            case "action":
                return <FragmentAction key={key} action={fragment} />;
            case "fileModification":
                return (
                    <FragmentFileModification
                        key={key}
                        fragment={fragment}
                        onHandleReveal={() => {
                            if (viewMode !== "vscode") {
                                setViewMode("vscode");
                            }
                            void vscodeIpcContext?.vscodeIpc.current?.openFile(fragment.filePath);
                        }}
                    />
                );
            case "environmentCreation":
                return <FragmentEnvironmentCreation key={key} environmentId={fragment.environmentId} />;
            case "thinking":
                return (
                    <Thinking key={key} fragment={fragment} onClick={onClick} data-tracking-id="thinking-fragment" />
                );
            case "hostAuthenticationRequired":
                return <FragmentHostAuthentication key={key} fragment={fragment} />;
            case "codeAnnotation":
                return (
                    <FragmentCodeAnnotation
                        key={key}
                        fragment={fragment}
                        onClick={() => {
                            onClick?.(fragment);
                            if (viewMode !== "vscode") {
                                setViewMode("vscode");
                            }
                            void vscodeIpcContext?.vscodeIpc.current?.openFile(fragment.filePath);
                        }}
                        data-tracking-id="code-annotation-vscode"
                    />
                );
            case "todoItem":
                // Todo items are handled at the grouping level, don't render them here
                return null;
            default:
                return null;
        }
    };

    return groupedMessages.map((group, groupIndex) => {
        const isLastGroup = groupIndex === groupedMessages.length - 1;
        const hasPreviousTodo = isPreviousTodo(groupIndex);
        const hasNextTodo = isNextTodo(groupIndex);
        const todosInBlock = totalNextTodos(groupIndex);
        const todosDoneInBlock = completeNextTodos(groupIndex);

        // Render "Ona's ToDos" header before the first todo
        const shouldShowTodoHeader = !hasPreviousTodo && totalTodos > 0;

        // Calculate fragment count for counter badge (steps within this todo)
        const fragmentCount = group.type === "todo-group" ? group.fragments.length : undefined;

        if (group.type === "user") {
            return (
                <div
                    key={group.message.id}
                    className={cn("flex w-full justify-end", getSpacingClass(groupIndex, false))}
                >
                    <ChatBubble
                        agentExecution={agentExecution}
                        message={group.message}
                        onClick={onClick}
                        isLast={isLastGroup}
                        agentMessages={filterMessagesByType(messages, MESSAGE_TYPES.AGENT)}
                        data-tracking-id-none
                    />
                </div>
            );
        } else if (group.type === "agent") {
            // Render agent message with ChatBubble (includes feedback buttons)
            return (
                <div key={group.message.id} className={cn("w-full", getSpacingClass(groupIndex, false))}>
                    <ChatBubble
                        agentExecution={agentExecution}
                        message={group.message}
                        onClick={onClick}
                        isLast={isLastGroup}
                        agentMessages={filterMessagesByType(messages, MESSAGE_TYPES.AGENT)}
                        data-tracking-id-none
                    />
                </div>
            );
        } else if (group.type === "todo-group") {
            // Find the current active fragment (last incomplete one when in progress)
            const isInProgress = group.todoStatus === AgentResponseBlock_TodoItem_Phase.IN_PROGRESS;
            const currentFragment =
                isInProgress && group.fragments.length > 0 ? group.fragments[group.fragments.length - 1] : null;

            // Extract text content for shimmer effect
            let currentFragmentText: string | undefined;
            if (isInProgress && group.fragments.length === 0) {
                // No fragments yet, Ona is thinking/planning
                currentFragmentText = "Thinking...";
            } else if (currentFragment && currentFragment.type === "text" && currentFragment.content) {
                currentFragmentText = currentFragment.content.split("\n")[0].substring(0, 100); // First line, max 100 chars
            } else if (currentFragment && currentFragment.type === "action" && currentFragment.intentDescription) {
                currentFragmentText = currentFragment.intentDescription;
            }

            return (
                <div key={group.todoId} className="w-full">
                    {shouldShowTodoHeader && (
                        <div className="mb-2 flex items-center gap-2">
                            <div className="rounded bg-surface-muted px-1.5 py-0.5 text-xs font-medium uppercase tracking-[0.5px] text-content-muted">
                                TODOs
                            </div>
                            <span className="text-sm font-medium text-content-muted">
                                {todosDoneInBlock}/{todosInBlock}
                            </span>
                        </div>
                    )}
                    <TodoContainer
                        todoId={group.todoId}
                        todoTitle={group.todoTitle}
                        todoStatus={group.todoStatus}
                        isLast={isLastGroup}
                        hasPreviousTodo={hasPreviousTodo}
                        hasNextTodo={hasNextTodo}
                        fragmentCount={fragmentCount}
                        fragments={group.fragments}
                        currentFragmentText={currentFragmentText}
                    >
                        <div className="flex flex-col gap-4">
                            {group.fragments.map((fragment, fragIndex) => {
                                const isLastFrag = fragIndex === group.fragments.length - 1;
                                return renderFragment(
                                    fragment,
                                    `${group.todoId}-${fragIndex}`,
                                    isLastGroup && isLastFrag,
                                );
                            })}
                        </div>
                    </TodoContainer>
                </div>
            );
        } else if (group.type === "agent-ungrouped") {
            // Render ungrouped fragments with ChatBubble wrapper (includes feedback buttons)
            const syntheticMessage: Message = {
                type: "agent",
                id: group.messageId,
                fragments: group.fragments,
            };
            return (
                <div key={`ungrouped-${groupIndex}`} className={cn("w-full", getSpacingClass(groupIndex, false))}>
                    <ChatBubble
                        agentExecution={agentExecution}
                        message={syntheticMessage}
                        onClick={onClick}
                        isLast={isLastGroup}
                        agentMessages={filterMessagesByType(messages, MESSAGE_TYPES.AGENT)}
                        data-tracking-id-none
                    />
                </div>
            );
        }
        return null;
    });
};
